import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  CHILD_LEVELS,
  childLevelOf,
  childMayUseNutrition,
  NOTE_VISIBILITIES,
} from '@kraftwerk/shared';
import type { ChildLevel, NoteVisibility, ReleasableChildModule } from '@kraftwerk/shared';
import { ApiError } from '../../api.js';
import { db } from '../../db/local.js';
import type { LocalHouseholdMember, LocalMemberNote } from '../../db/local.js';
import {
  createChild,
  createChildCode,
  deleteMemberNote,
  notesAbout,
  removeChild,
  saveMemberNote,
  setChildLevel,
  setTeenNutrition,
  setTeenTraining,
  childWater,
  setChildWater,
} from '../../db/children.js';
import { syncOnce } from '../../sync/engine.js';
import { useToday } from '../../hooks/useToday.js';
import { formatDay } from '../../format.js';
import { PocketLedger } from './Pocket.js';
import { ChildLocationSetting } from './Location.js';
import { RecordPanel } from '../records/RecordPanel.js';

/**
 * Kinder im Haushalt (E16): anlegen, Gerät anmelden, Notizen, entfernen.
 *
 * Nur Erwachsene sehen diesen Bereich. Anlegen und Codes gehen online —
 * Mitgliedschaften schreibt nur der Server; Notizen gehen über den Abgleich.
 */
export function ChildrenSection({
  householdId,
  children,
  onChanged,
}: {
  householdId: string;
  children: LocalHouseholdMember[];
  onChanged: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section aria-labelledby="children-heading" className="kw-tile flex flex-col gap-3">
      <div>
        <h3 id="children-heading" className="font-medium">
          {t('children.heading')}
        </h3>
        <p className="mt-1 text-xs text-ink-muted">{t('children.intro')}</p>
      </div>
      {children.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {children.map((child) => (
            <li key={child.userId} className="flex flex-col gap-2">
              <button
                type="button"
                aria-expanded={open === child.userId}
                onClick={() => setOpen(open === child.userId ? null : child.userId)}
                className="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-line px-3 py-2 text-left"
              >
                <span className="text-sm font-medium">
                  {child.displayName}
                  {child.birthYear ? (
                    <span className="ml-2 text-xs font-normal text-ink-muted">
                      {t('children.age', { age: new Date().getFullYear() - child.birthYear })}
                    </span>
                  ) : null}
                </span>
                <span aria-hidden="true" className="text-ink-muted">
                  {open === child.userId ? '−' : '+'}
                </span>
              </button>
              {open === child.userId ? (
                <ChildPanel householdId={householdId} child={child} onChanged={onChanged} />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <AddChild onAdded={onChanged} />
    </section>
  );
}

function AddChild({ onAdded }: { onAdded: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const nameId = useId();
  const yearId = useId();
  const [name, setName] = useState('');
  const [year, setYear] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      className="flex flex-col gap-2 border-t border-line pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim()) return;
        const birthYear = year.trim() ? Number(year) : null;
        setBusy(true);
        setError(null);
        createChild({
          name: name.trim(),
          birthYear: Number.isInteger(birthYear) ? birthYear : null,
        })
          .then(() => {
            setName('');
            setYear('');
            onAdded();
          })
          .catch(() => setError(t('children.offline')))
          .finally(() => setBusy(false));
      }}
    >
      <p className="kw-label">{t('children.add')}</p>
      <div className="flex gap-2">
        <label htmlFor={nameId} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs text-ink-muted">{t('children.name')}</span>
          <input
            id={nameId}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            maxLength={60}
            className="kw-field"
          />
        </label>
        <label htmlFor={yearId} className="flex w-24 flex-col gap-1">
          <span className="text-xs text-ink-muted">{t('children.birthYear')}</span>
          <input
            id={yearId}
            inputMode="numeric"
            value={year}
            onChange={(event) => setYear(event.target.value)}
            className="kw-field"
          />
        </label>
      </div>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
      <button type="submit" disabled={busy || !name.trim()} className="kw-btn kw-btn-small">
        {t('children.addSubmit')}
      </button>
    </form>
  );
}

function ChildPanel({
  householdId,
  child,
  onChanged,
}: {
  householdId: string;
  child: LocalHouseholdMember;
  onChanged: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-surface-raised p-3">
      <LevelPicker child={child} onChanged={onChanged} />
      <TeenNutritionSetting child={child} />
      <ChildWaterSetting child={child} />
      <TeenTrainingSetting child={child} />
      <div className="flex flex-col gap-2">
        <p className="kw-label">{t('pocket.heading')}</p>
        <PocketLedger child={child} mode="parent" mayWrite />
      </div>
      <div className="flex flex-col gap-2">
        <p className="kw-label">{t('location.heading')}</p>
        <ChildLocationSetting child={child} />
      </div>
      <div className="flex flex-col gap-2">
        <p className="kw-label">{t('record.heading')}</p>
        <p className="text-xs text-ink-muted">
          {t('record.childIntro', { name: child.displayName })}
        </p>
        <RecordPanel
          subjectId={child.userId}
          householdId={householdId}
          mode="child"
          name={child.displayName}
        />
      </div>
      <DeviceCode child={child} />
      <ChildNotes householdId={householdId} child={child} />
      <RemoveChild child={child} onRemoved={onChanged} />
      <p className="text-xs text-ink-muted">{t('children.forWhomHint')}</p>
    </div>
  );
}

/**
 * Stufe (E20): Was das Kind selbst anlegen darf, entscheiden die Eltern —
 * das Alter hat beim Anlegen nur vorgeschlagen.
 */
function LevelPicker({
  child,
  onChanged,
}: {
  child: LocalHouseholdMember;
  onChanged: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const current = childLevelOf(child.childLevel);

  const choose = (level: ChildLevel) => {
    if (level === current) return;
    setError(null);
    setChildLevel(child.userId, level)
      .then(onChanged)
      .catch(() => setError(t('children.offline')));
  };

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="kw-label">{t('children.level.heading')}</legend>
      <div className="flex flex-wrap gap-2">
        {CHILD_LEVELS.map((level) => (
          <button
            key={level}
            type="button"
            aria-pressed={current === level}
            onClick={() => choose(level)}
            className="kw-toggle"
          >
            {t(`children.level.${level}`)}
          </button>
        ))}
      </div>
      <p className="text-xs text-ink-muted">
        {t(`children.level.${current}Hint`, { name: child.displayName })}
      </p>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

/**
 * Ernährung (27.10.2026) und Training (28.10.2026) für ein Teen: je ein
 * Schalter, ab Werk aus, schalten nur die Eltern — und nur bei Stufe
 * „jugendlich“. Darunter steht, warum der Schalter fehlt.
 */
export function TeenReleaseSetting({
  child,
  module,
}: {
  child: LocalHouseholdMember;
  module: ReleasableChildModule;
}): React.JSX.Element {
  const { t } = useTranslation();
  const teen = childLevelOf(child.childLevel) === 'teen';
  const on = teen && (module === 'nutrition' ? child.teenNutrition : child.teenTraining) === true;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const toggle = () => {
    setBusy(true);
    setError(false);
    (module === 'nutrition' ? setTeenNutrition : setTeenTraining)(child.userId, !on)
      // Die Mitgliedschaft schreibt der Server — erst der Abgleich zeigt den neuen Stand.
      .then(() => syncOnce())
      .catch(() => setError(true))
      .finally(() => setBusy(false));
  };
  return (
    <div className="flex flex-col gap-1">
      <p className="kw-label">{t(`children.${module}.heading`)}</p>
      {teen ? (
        <button
          type="button"
          aria-pressed={on}
          disabled={busy}
          onClick={toggle}
          className="kw-toggle self-start"
        >
          {t(`children.${module}.toggle`)}
        </button>
      ) : null}
      <p className="text-xs text-ink-muted">
        {!teen
          ? t(`children.${module}.onlyTeen`, { name: child.displayName })
          : on
            ? t(`children.${module}.onHint`, { name: child.displayName })
            : t(`children.${module}.offHint`, { name: child.displayName })}
      </p>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {t('children.offline')}
        </p>
      ) : null}
    </div>
  );
}

export function TeenNutritionSetting({
  child,
}: {
  child: LocalHouseholdMember;
}): React.JSX.Element {
  return <TeenReleaseSetting child={child} module="nutrition" />;
}

export function TeenTrainingSetting({ child }: { child: LocalHouseholdMember }): React.JSX.Element {
  return <TeenReleaseSetting child={child} module="training" />;
}

/**
 * Wasserziel eines Kindes (28.10.2026): Vorgabe 1500 ml, die Eltern stellen
 * es ein, das Kind darf es danach selbst ändern. Wasser gehört zur
 * Ernährung — ohne deren Freigabe steht hier, warum es fehlt. Das Profil des
 * Kindes liegt nicht auf diesem Gerät: gelesen und geschrieben beim Server.
 */
export function ChildWaterSetting({
  child,
}: {
  child: LocalHouseholdMember;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const fieldId = useId();
  const nutrition = childMayUseNutrition({
    level: child.childLevel,
    nutrition: child.teenNutrition,
  });
  const [current, setCurrent] = useState<{ targetMl: number; custom: boolean } | null>(null);
  const [value, setValue] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'saved' | 'invalid' | 'offline'>('idle');

  useEffect(() => {
    if (!nutrition) return;
    let gone = false;
    childWater(child.userId)
      .then((water) => {
        if (gone) return;
        setCurrent(water);
        setValue(String(water.targetMl));
      })
      .catch(() => {
        if (!gone) setState('offline');
      });
    return () => {
      gone = true;
    };
  }, [child.userId, nutrition]);

  // Unter „jugendlich“ sagt der Schalter für Ernährung schon, warum sie fehlt.
  if (childLevelOf(child.childLevel) !== 'teen') return null;
  if (!nutrition) {
    return (
      <div className="flex flex-col gap-1">
        <p className="kw-label">{t('children.water.heading')}</p>
        <p className="text-xs text-ink-muted">
          {t('children.water.needsNutrition', { name: child.displayName })}
        </p>
      </div>
    );
  }

  const save = () => {
    const ml = Number(value.trim());
    if (!Number.isInteger(ml) || ml < 250 || ml > 10_000) {
      setState('invalid');
      return;
    }
    setState('busy');
    setChildWater(child.userId, ml)
      .then(() => {
        setCurrent({ targetMl: ml, custom: true });
        setState('saved');
      })
      .catch(() => setState('offline'));
  };

  return (
    <form
      className="flex flex-col gap-1"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <label htmlFor={fieldId} className="kw-label">
        {t('children.water.heading')}
      </label>
      <div className="flex items-end gap-2">
        <input
          id={fieldId}
          inputMode="numeric"
          value={value}
          disabled={current === null}
          onChange={(event) => setValue(event.target.value)}
          className="kw-field w-28"
        />
        <button
          type="submit"
          disabled={current === null || state === 'busy'}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('children.water.save')}
        </button>
      </div>
      <p className="text-xs text-ink-muted">
        {current === null
          ? state === 'offline'
            ? null
            : t('children.water.loading')
          : current.custom
            ? t('children.water.current', { ml: current.targetMl, name: child.displayName })
            : t('children.water.default', { ml: current.targetMl, name: child.displayName })}
      </p>
      {state === 'saved' ? (
        <p role="status" className="text-xs text-good">
          {t('children.water.saved', { name: child.displayName })}
        </p>
      ) : null}
      {state === 'invalid' ? (
        <p role="alert" className="text-xs text-danger">
          {t('children.water.invalid')}
        </p>
      ) : null}
      {state === 'offline' ? (
        <p role="alert" className="text-xs text-danger">
          {t('children.offline')}
        </p>
      ) : null}
    </form>
  );
}

/** Einmal-Code fürs Gerät des Kindes — 15 Minuten, genau einmal. */
function DeviceCode({ child }: { child: LocalHouseholdMember }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-2">
      <p className="kw-label">{t('children.device')}</p>
      <p className="text-xs text-ink-muted">
        {t('children.deviceHint', { name: child.displayName })}
      </p>
      {code ? (
        <p role="status" className="flex flex-col gap-1">
          <span className="kw-figure text-3xl select-all">{code.code}</span>
          <span className="text-xs text-ink-muted">
            {t('children.codeExpires', {
              time: new Date(code.expiresAt).toLocaleTimeString(i18n.language, {
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}
          </span>
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        onClick={() => {
          setError(null);
          createChildCode(child.userId)
            .then(setCode)
            .catch(() => setError(t('children.offline')));
        }}
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {code ? t('children.newCode') : t('children.makeCode')}
      </button>
    </div>
  );
}

/**
 * Notizen über das Kind. Die Sichtbarkeit steht an jeder Notiz — wer eine
 * „nur ich“-Notiz schreibt, soll nicht rätseln müssen, ob das Kind sie sieht.
 */
function ChildNotes({
  householdId,
  child,
}: {
  householdId: string;
  child: LocalHouseholdMember;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const today = useToday();
  const bodyId = useId();
  const notes = useLiveQuery(
    () => notesAbout(child.userId, db),
    [child.userId],
    [] as LocalMemberNote[],
  );
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>('me');

  return (
    <div className="flex flex-col gap-2">
      <p className="kw-label">{t('children.notes')}</p>
      {notes.length === 0 ? (
        <p className="text-xs text-ink-muted">{t('children.noNotes')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {notes.map((note) => (
            <li key={note.id} className="rounded-lg border border-line p-2">
              <p className="text-xs text-ink-muted">
                {formatDay(note.date, 'short', i18n.language)} ·{' '}
                {t(`children.visibility.${note.visibility}`, { name: child.displayName })}
              </p>
              <p className="mt-1 text-sm whitespace-pre-wrap">{note.body}</p>
              <button
                type="button"
                onClick={() => void deleteMemberNote(note, db)}
                className="mt-1 py-1 text-xs text-ink-muted underline underline-offset-2"
              >
                {t('children.deleteNote')}
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!body.trim() || !today) return;
          void saveMemberNote(
            { householdId, aboutUserId: child.userId, visibility, date: today, body },
            null,
            db,
          ).then(() => setBody(''));
        }}
      >
        <label htmlFor={bodyId} className="sr-only">
          {t('children.noteBody', { name: child.displayName })}
        </label>
        <textarea
          id={bodyId}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={2}
          placeholder={t('children.noteBody', { name: child.displayName })}
          className="kw-field"
        />
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs text-ink-muted">{t('children.whoSees')}</legend>
          <div className="flex flex-wrap gap-2">
            {NOTE_VISIBILITIES.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={visibility === option}
                onClick={() => setVisibility(option)}
                className="kw-toggle"
              >
                {t(`children.visibility.${option}`, { name: child.displayName })}
              </button>
            ))}
          </div>
        </fieldset>
        <button type="submit" disabled={!body.trim()} className="kw-btn kw-btn-small">
          {t('children.saveNote')}
        </button>
      </form>
    </div>
  );
}

function RemoveChild({
  child,
  onRemoved,
}: {
  child: LocalHouseholdMember;
  onRemoved: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="self-start py-2 text-xs text-danger underline underline-offset-2"
      >
        {t('children.remove', { name: child.displayName })}
      </button>
    );
  }
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-danger p-2">
      <p className="text-sm">{t('children.removeWarning', { name: child.displayName })}</p>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() =>
            removeChild(child.userId)
              .then(onRemoved)
              .catch((cause: unknown) =>
                setError(
                  cause instanceof ApiError && cause.status === 404
                    ? t('children.gone')
                    : t('children.offline'),
                ),
              )
          }
          className="kw-btn kw-btn-small flex-1 bg-danger"
        >
          {t('children.removeConfirm')}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('nutrition.library.cancel')}
        </button>
      </div>
    </div>
  );
}

/**
 * Auf „Heute“ eines Kindes (E16): Nachrichten der Eltern („auch das Kind“).
 * Auf dem Gerät liegt ohnehin nur, was das Kind sehen darf — der Server
 * filtert beim Holen.
 */
export function ForMeTile({ me }: { me: string }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const notes = useLiveQuery(() => notesAbout(me, db), [me], [] as LocalMemberNote[]);
  const recent = notes.filter((note) => note.visibility === 'child').slice(0, 3);
  if (recent.length === 0) return null;
  return (
    <section aria-labelledby="for-me-heading" className="kw-tile flex flex-col gap-2">
      <h2 id="for-me-heading" className="kw-kicker">
        {t('children.forMe')}
      </h2>
      <ul className="flex flex-col gap-2">
        {recent.map((note) => (
          <li key={note.id}>
            <p className="text-xs text-ink-muted">{formatDay(note.date, 'short', i18n.language)}</p>
            <p className="text-sm whitespace-pre-wrap">{note.body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
